'use client';

import React from 'react';

// ── Fire danger calculation ─────────────────────────────────────
// Simplified AFDRS-inspired calculation from Open-Meteo weather data
// This is an ESTIMATE — always check official sources before travelling

export interface FireDangerDay {
  date: string;
  tempMax: number;
  humidityMin: number;
  windMax: number;
  precipSum: number;
}

export type FireLevel = 'no_rating' | 'low_moderate' | 'high' | 'extreme' | 'catastrophic';

interface FireLevelInfo {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
  textColor: string;
}

export const FIRE_LEVELS: Record<FireLevel, FireLevelInfo> = {
  no_rating:    { label: 'No Rating',     color: '#6b7280', bgColor: '#f3f4f6', borderColor: '#e5e7eb', dotColor: '#9ca3af', textColor: '#6b7280' },
  low_moderate: { label: 'Low–Moderate',   color: '#16a34a', bgColor: '#f0fdf4', borderColor: '#bbf7d0', dotColor: '#22c55e', textColor: '#15803d' },
  high:         { label: 'High',           color: '#d97706', bgColor: '#fffbeb', borderColor: '#fde68a', dotColor: '#f59e0b', textColor: '#b45309' },
  extreme:      { label: 'Extreme',        color: '#dc2626', bgColor: '#fef2f2', borderColor: '#fecaca', dotColor: '#ef4444', textColor: '#b91c1c' },
  catastrophic: { label: 'Catastrophic',   color: '#7f1d1d', bgColor: '#450a0a', borderColor: '#991b1b', dotColor: '#dc2626', textColor: '#fecaca' },
};

export function calculateFireLevel(day: FireDangerDay): FireLevel {
  const { tempMax, humidityMin, windMax, precipSum } = day;

  // Recent rain significantly reduces fire danger
  if (precipSum > 10) return 'no_rating';
  if (precipSum > 5 && tempMax < 25) return 'no_rating';

  // Catastrophic: extreme heat + very low humidity + strong wind
  if (tempMax >= 40 && humidityMin < 12 && windMax > 45) return 'catastrophic';
  if (tempMax >= 42 && humidityMin < 15 && windMax > 35) return 'catastrophic';

  // Extreme: very hot + low humidity + strong wind
  if (tempMax >= 37 && humidityMin < 15 && windMax > 35) return 'extreme';
  if (tempMax >= 35 && humidityMin < 12 && windMax > 30) return 'extreme';

  // High: hot + moderate-low humidity + wind
  if (tempMax >= 32 && humidityMin < 25 && windMax > 25) return 'high';
  if (tempMax >= 30 && humidityMin < 20 && windMax > 20) return 'high';
  if (tempMax >= 35 && humidityMin < 30) return 'high';

  // Low-Moderate: warm + somewhat dry
  if (tempMax >= 22 && humidityMin < 50 && precipSum < 2) return 'low_moderate';

  return 'no_rating';
}

// ── Fire authority links by state ───────────────────────────────
export const STATE_FIRE_URLS: Record<string, { name: string; url: string }> = {
  NSW: { name: 'NSW RFS', url: 'https://www.rfs.nsw.gov.au/fire-information/fires-near-me' },
  VIC: { name: 'CFA Victoria', url: 'https://www.cfa.vic.gov.au/warnings-restrictions/total-fire-bans-and-ratings' },
  QLD: { name: 'QFES', url: 'https://www.qfes.qld.gov.au/current-incidents' },
  SA:  { name: 'SA CFS', url: 'https://www.cfs.sa.gov.au/warnings-and-incidents' },
  WA:  { name: 'DFES WA', url: 'https://www.dfes.wa.gov.au/warnings' },
  TAS: { name: 'TFS', url: 'https://www.fire.tas.gov.au/' },
  NT:  { name: 'NT Fire', url: 'https://securent.nt.gov.au/alerts' },
  ACT: { name: 'ACT ESA', url: 'https://esa.act.gov.au/fire-and-rescue' },
};

// ── Component ───────────────────────────────────────────────────

interface FireDangerWidgetProps {
  days: FireDangerDay[];
  state?: string;
}

function formatDayLabel(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-AU', { weekday: 'short' });
}

function FireLevelBadge({ level, compact = false }: { level: FireLevel; compact?: boolean }) {
  const info = FIRE_LEVELS[level];
  const isCatastrophic = level === 'catastrophic';

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-semibold rounded-md ${compact ? 'text-[11px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}`}
      style={{
        backgroundColor: info.bgColor,
        color: info.textColor,
        border: `1px solid ${info.borderColor}`,
        ...(isCatastrophic ? { color: '#fef2f2' } : {}),
      }}
    >
      <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none"
        style={level === 'extreme' || level === 'catastrophic' ? { animation: 'fire-pulse 2s ease-in-out infinite' } : {}}>
        <path d="M12 2C12 2 4 12.5 4 16a8 8 0 0016 0c0-3.5-8-14-8-14z" fill={info.dotColor} />
        <path d="M12 12c0 0-3 3-3 5a3 3 0 006 0c0-2-3-5-3-5z" fill={info.bgColor} opacity={0.7} />
      </svg>
      {info.label}
    </span>
  );
}

export default function FireDangerWidget({ days, state }: FireDangerWidgetProps) {
  if (!days || days.length === 0) return null;

  const levels = days.slice(0, 3).map(d => ({
    ...d,
    level: calculateFireLevel(d),
    label: formatDayLabel(d.date),
  }));

  // Don't show if all days are no_rating
  const allNoRating = levels.every(l => l.level === 'no_rating');

  // Determine highest danger in the forecast
  const levelOrder: FireLevel[] = ['no_rating', 'low_moderate', 'high', 'extreme', 'catastrophic'];
  const highestLevel = levels.reduce((max, l) => {
    return levelOrder.indexOf(l.level) > levelOrder.indexOf(max) ? l.level : max;
  }, 'no_rating' as FireLevel);

  const highestInfo = FIRE_LEVELS[highestLevel];
  const fireAuthority = state ? STATE_FIRE_URLS[state] : null;
  const isElevated = highestLevel === 'high' || highestLevel === 'extreme' || highestLevel === 'catastrophic';

  return (
    <div
      className="rounded-lg overflow-hidden transition-all"
      style={{
        border: `1px solid ${isElevated ? highestInfo.borderColor : '#e5e7eb'}`,
        background: '#ffffff',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{
          background: isElevated
            ? `linear-gradient(135deg, ${highestInfo.bgColor}, #ffffff)`
            : '#fafafa',
          borderBottom: `1px solid ${isElevated ? highestInfo.borderColor : '#f3f4f6'}`,
        }}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient id={`flame-${highestLevel}`} x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor={highestLevel === 'no_rating' ? '#9ca3af' : highestLevel === 'low_moderate' ? '#22c55e' : highestLevel === 'high' ? '#f59e0b' : highestLevel === 'extreme' ? '#ef4444' : '#991b1b'} />
                <stop offset="100%" stopColor={highestLevel === 'no_rating' ? '#d1d5db' : highestLevel === 'low_moderate' ? '#86efac' : highestLevel === 'high' ? '#fbbf24' : highestLevel === 'extreme' ? '#f87171' : '#dc2626'} />
              </linearGradient>
            </defs>
            <path
              d="M12 2C12 2 4 12.5 4 16a8 8 0 0016 0c0-3.5-8-14-8-14z"
              fill={`url(#flame-${highestLevel})`}
            />
            <path
              d="M12 12c0 0-3 3-3 5a3 3 0 006 0c0-2-3-5-3-5z"
              fill={highestLevel === 'no_rating' ? '#f3f4f6' : highestLevel === 'low_moderate' ? '#dcfce7' : highestLevel === 'high' ? '#fef3c7' : '#fef2f2'}
              opacity={0.8}
            />
          </svg>
          <span
            className="text-xs font-bold uppercase tracking-wider"
            style={{ color: isElevated ? highestInfo.color : '#6b7280', fontFamily: 'system-ui, sans-serif' }}
          >
            Fire Danger
          </span>
        </div>
        {!allNoRating && <FireLevelBadge level={highestLevel} compact />}
      </div>

      {/* Day rows */}
      <div className="px-4 py-2.5 divide-y divide-gray-100">
        {levels.map((day, i) => (
          <div
            key={day.date}
            className={`flex items-center justify-between ${i === 0 ? 'pb-2' : i === levels.length - 1 ? 'pt-2' : 'py-2'}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className={`text-xs ${i === 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-500'}`} style={{ width: '64px' }}>
                {day.label}
              </span>
              <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span>{Math.round(day.tempMax)}&deg;</span>
                <span>&middot;</span>
                <span>{Math.round(day.humidityMin)}%</span>
                <span>&middot;</span>
                <span>{Math.round(day.windMax)}km/h</span>
              </div>
            </div>
            <FireLevelBadge level={day.level} compact />
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: '#fafafa', borderTop: '1px solid #f3f4f6' }}
      >
        <span className="text-[10px] text-gray-400" style={{ lineHeight: 1.3 }}>
          Estimated from forecast conditions
        </span>
        {fireAuthority ? (
          <a
            href={fireAuthority.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-semibold hover:underline flex items-center gap-0.5"
            style={{ color: '#1a68d1' }}
          >
            {fireAuthority.name}
            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        ) : (
          <span className="text-[10px] text-gray-400">Check local fire authority</span>
        )}
      </div>

    </div>
  );
}
